import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { SearchQueryOrder, SearchQueryRequestedPage } from "types/generated";

import { SEARCH_SPACE_POLICIES, SEARCH_SPACE_POLICIES_COUNT } from "./gql";
import { SpaceDetailsPolicy } from "./types";

type UseSpaceDetailsPoliciesProps = {
  spaceId: string;
  orderBy: SearchQueryOrder;
  first: number;
  fullTextSearch: string | null;
};

const useSpaceDetailsPolicies = ({
  spaceId,
  orderBy,
  first,
  fullTextSearch,
}: UseSpaceDetailsPoliciesProps) => {
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const resetKey = `${spaceId}:${orderBy.field}:${orderBy.direction}:${fullTextSearch ?? ""}`;
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  if (previousResetKey !== resetKey) {
    setPreviousResetKey(resetKey);
    setRequestedPage(null);
    setAfter(null);
  }

  const variables = useMemo(
    () => ({
      space: spaceId,
      input: {
        predicates: [],
        orderBy: [orderBy],
        fullTextSearch,
        first,
        after,
        requestedPage,
      },
    }),
    [spaceId, orderBy, first, after, requestedPage, fullTextSearch]
  );

  const { error, loading, data, networkStatus, previousData } = usePolledQuery(
    SEARCH_SPACE_POLICIES,
    {
      variables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const { data: countData, previousData: previousCountData } = usePolledQuery(
    SEARCH_SPACE_POLICIES_COUNT,
    {
      variables: { space: spaceId, input: { fields: [], fullTextSearch, predicates: [] } },
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const loadPolicies = useCallback(
    ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      setAfter(cursor);
      setRequestedPage(requestedPage);
    },
    []
  );

  const initialLoading =
    loading && networkStatus === NetworkStatus.loading && !data?.searchSpacePolicies;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const policies = useMemo<SpaceDetailsPolicy[]>(() => {
    const edges =
      data?.searchSpacePolicies?.edges || previousData?.searchSpacePolicies?.edges || [];

    return edges.map(({ inherited, node }) => ({
      id: node.id,
      name: node.name,
      description: node.description,
      type: node.type,
      body: node.body,
      inheritedFrom: inherited ? { id: node.spaceDetails.id, name: node.spaceDetails.name } : null,
    }));
  }, [data?.searchSpacePolicies?.edges, previousData?.searchSpacePolicies?.edges]);

  const totalCount =
    countData?.searchSpacePoliciesSuggestions.filteredCount ??
    previousCountData?.searchSpacePoliciesSuggestions.filteredCount ??
    0;

  return {
    loadPolicies,
    error,
    policies,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor: data?.searchSpacePolicies?.pageInfo.endCursor,
    startCursor: data?.searchSpacePolicies?.pageInfo.startCursor,
    paginationResetKey: resetKey,
  };
};

export default useSpaceDetailsPolicies;
